<script setup lang="ts">
import { computed, ref } from "vue";

// Types
import type { ButtonAltProps } from "@/dsComponents/buttons/types";

// Components
import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";

interface AsideSectionTitleProps {
    title: string;
    size?: "sm" | "md";
    type?: "default" | "text";
    count?: number;
    readOnly?: boolean;
    actions?: ButtonAltProps[];
    disableOpenClose?: boolean;
    defaultOpen?: boolean;
}

const props = withDefaults(defineProps<AsideSectionTitleProps>(), {
    size: "sm",
    type: "default",
    defaultOpen: false,
});

const emits = defineEmits<{
    (event: "toggleSection", value: boolean): void;
    (event: "actionClick", value: { index: number }): void;
}>();

const isOpenSection = ref(props.defaultOpen);

const handleOpenCloseSection = () => {
    if (props.disableOpenClose) return;
    isOpenSection.value = !isOpenSection.value;
    emits("toggleSection", isOpenSection.value);
};

const handleActionClick = (index: number) => {
    emits("actionClick", { index });
};

const rightIcon = computed(() => {
    if (props.readOnly || props.disableOpenClose) return "";
    return isOpenSection.value ? "caret-down-filled" : "caret-right-filled";
});
const isTypeText = computed(() => props.type === "text");
</script>
<template>
    <div class="aside-section-title-container">
        <div class="title-count">
            <div v-if="isTypeText" class="aside-label">{{ title }}</div>
            <ButtonAlt
                v-else
                :size="size"
                variant="ghost"
                type="neutral"
                :rightIcon="rightIcon"
                :label="title"
                @click="handleOpenCloseSection"
                :disabled="readOnly"
            />
            <div v-if="count" class="aside-count" :class="[size]">
                {{ count.toString() }}
            </div>
        </div>
        <div class="aside-actions" v-if="actions">
            <ButtonAlt v-for="(action, index) in actions" :key="index" v-bind="{ ...action, size }" @click="handleActionClick(index)" />
        </div>
        <slot name="actions"></slot>
    </div>
</template>
<style scoped lang="scss">
.aside-section-title-container {
    display: flex;
    width: 100%;
    justify-content: center;
    align-items: center;
    gap: 8px;

    .title-count {
        display: flex;
        align-items: center;
        gap: 8px;
        flex: 1 0 0;

        .aside-label {
            display: flex;
            min-height: 36px;
            padding-right: 12px;
            align-items: center;
            gap: 10px;

            color: $neutral-700;
            font-family: Inter;
            font-style: normal;
            font-weight: 500;
            @include font-size-14;
        }

        .aside-count {
            color: $neutral-600;
            font-family: Inter;
            font-style: normal;
            font-weight: 400;
            &.sm {
                @include font-size-12;
            }

            &.md {
                @include font-size-13;
            }
        }
    }

    .aside-actions {
        display: flex;
        padding: 0px 4px;
        align-items: center;
        gap: 2px;
    }
}
</style>
